"use client";

import Link from "next/link";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { PageSection } from "@/types";

interface HeroProps {
  section: PageSection;
}

export function Hero({ section }: HeroProps) {
  return (
    <section className="relative flex min-h-[calc(100dvh-4rem)] w-full items-center justify-center overflow-hidden bg-black">
      {/* Video Background */}
      {section.videoUrl && (
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 h-full w-full object-cover scale-[1.10]"
        >
          <source src={section.videoUrl} type="video/mp4" />
        </video>
      )}

      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 text-center text-white">
        <h1 className="mb-4 text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
          {(section.title || "").split("**").map((part, i) =>
            i % 2 === 1 ? (
              <span
                key={i}
                className="bg-linear-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent"
              >
                {part}
              </span>
            ) : (
              <span key={i}>{part}</span>
            )
          )}
        </h1>

        {section.subtitle && (
          <p className="mx-auto mb-8 max-w-2xl text-lg text-white/80 sm:text-xl">
            {section.subtitle}
          </p>
        )}

        {/* CTA Buttons */}
        {section.buttons && section.buttons.length > 0 && (
          <div className="flex flex-wrap items-center justify-center gap-4">
            {section.buttons.map((btn, i) => (
              <Link
                key={i}
                href={btn.url}
                className={cn(
                  buttonVariants({ size: "lg", variant: btn.variant === "outline" ? "outline" : "default" }),
                  "px-8 py-6 text-lg font-semibold",
                  btn.variant === "outline"
                    ? "border-2 border-white! text-white! bg-transparent! hover:bg-white! hover:text-black!"
                    : "bg-orange-600! hover:bg-orange-700! text-white!"
                )}
              >
                {btn.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
